import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/shell/PageHeader";
import { useAppState } from "@/state/AppStateProvider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { calendarApi } from "@/lib/apiClient";
import { CalendarItem } from "@/components/content/CalendarItem";
import { ScheduleAssetDialog } from "@/components/content/ScheduleAssetDialog";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PLATFORM_LABELS, PLATFORMS } from "@/lib/constants";
import { buildCalendarWorkspace } from "@/lib/calendarWorkspace";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CalendarRange,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  Copy,
  ExternalLink,
  Info,
  RadioTower,
  RefreshCw,
  Route,
  Send,
  Loader2,
} from "lucide-react";
import { formatDateTime, formatTime, formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function startOfWeek(d) { const x = new Date(d); const day = x.getDay(); x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }
function dayKey(d) { return d.toDateString(); }

function initialCalendarCursor(items) {
  const now = Date.now();
  const upcoming = [...items]
    .filter((item) => new Date(item.scheduled_at).getTime() >= now && item.status !== "published")
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
  return upcoming ? new Date(upcoming.scheduled_at) : new Date();
}

export default function CalendarPage() {
  const { state, rescheduleCalendarItem, publishCalendarItem, armCalendarItem, publishAsset } = useAppState();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(() => initialCalendarCursor(state.calendar));
  const [selected, setSelected] = useState(null);
  const [platform, setPlatform] = useState("all");
  const [hoverDay, setHoverDay] = useState(null);
  const [dragging, setDragging] = useState(null);
  // Auto-publish engine state (enabled flag + armed/due counts), best-effort.
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    calendarApi.scheduler().then((s) => { if (alive) setSchedulerStatus(s); }).catch(() => {});
    return () => { alive = false; };
  }, [state.calendar]);

  const workspace = useMemo(() => buildCalendarWorkspace({
    calendar: state.calendar,
    assets: state.assets,
    integrations: state.integrations,
  }), [state.calendar, state.assets, state.integrations]);

  const filteredItems = useMemo(() => {
    const list = platform === "all"
      ? workspace.items
      : workspace.items.filter((item) => item.platform === platform);
    return [...list].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }, [workspace.items, platform]);

  const selectedAsset = useMemo(
    () => (selected?.asset_id ? state.assets.find((asset) => asset.id === selected.asset_id) : null),
    [selected, state.assets],
  );

  useEffect(() => {
    if (!selected?.id) return;
    const latest = workspace.items.find((item) => item.id === selected.id);
    if (!latest) return;
    if (
      latest.status !== selected.status ||
      latest.scheduled_at !== selected.scheduled_at ||
      latest.scheduled_account_label !== selected.scheduled_account_label
    ) {
      setSelected(latest);
    }
  }, [selected, workspace.items]);

  const monthCells = useMemo(() => {
    const first = startOfMonth(cursor);
    const startDay = first.getDay();
    const start = addDays(first, -startDay);
    return Array.from({ length: 42 }).map((_, i) => addDays(start, i));
  }, [cursor]);

  const weekDays = useMemo(() => {
    const start = new Date(cursor);
    start.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }).map((_, i) => addDays(start, i));
  }, [cursor]);

  const itemsByDay = useMemo(() => {
    const map = new Map();
    filteredItems.forEach((item) => {
      const key = dayKey(new Date(item.scheduled_at));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return map;
  }, [filteredItems]);

  const handleDrop = (event, targetDate) => {
    event.preventDefault();
    const id = event.dataTransfer.getData("application/x-calendar-item");
    if (!id) return;
    const original = workspace.items.find((item) => item.id === id);
    if (!original) return;
    const origDate = new Date(original.scheduled_at);
    const newDate = new Date(targetDate);
    newDate.setHours(origDate.getHours(), origDate.getMinutes(), 0, 0);
    if (sameDay(origDate, newDate)) {
      setDragging(null);
      setHoverDay(null);
      return;
    }
    rescheduleCalendarItem(id, newDate.toISOString());
    toast.success(`Rescheduled to ${formatDate(newDate, { year: false })} at ${formatTime(newDate)}`);
    setDragging(null);
    setHoverDay(null);
  };

  const shiftCursor = (direction) => {
    const delta = view === "month" ? 30 : 7;
    setCursor((current) => addDays(current, direction * delta));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="calendar-page">
      <PageHeader
        eyebrow="Calendar"
        title="Publishing command calendar."
        description="See what ships next, which channels are connected, and where content still needs a human decision before it goes live."
        actions={(
          <>
            <Button size="sm" className="h-9 gap-1.5" onClick={() => setCursor(new Date())} data-testid="calendar-hero-today">
              <RefreshCw className="h-4 w-4" /> Today
            </Button>
            <Button asChild size="sm" variant="outline" className="h-9 gap-1.5">
              <Link to="/library"><ClipboardCheck className="h-4 w-4" /> Review queue</Link>
            </Button>
          </>
        )}
        testId="calendar-page-header"
      />

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <main className="space-y-4">
          <CalendarToolbar
            view={view}
            setView={setView}
            platform={platform}
            setPlatform={setPlatform}
            cursor={cursor}
            onPrev={() => shiftCursor(-1)}
            onNext={() => shiftCursor(1)}
            onToday={() => setCursor(new Date())}
          />

          {(state.calendar || []).length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-8 text-center" style={{ borderColor: "var(--border-subtle)" }} data-testid="calendar-empty">
              <CalendarClock className="h-6 w-6 text-[color:var(--accent-cyan)]" />
              <p className="text-sm font-semibold text-1">Nothing scheduled yet</p>
              <p className="max-w-md text-xs text-muted-foreground">Approve a draft in the Library and schedule it — it'll show up here on the calendar.</p>
              <Button asChild size="sm" className="mt-1 gap-1.5"><Link to="/library" data-testid="calendar-empty-cta">Go to Library <ArrowRight className="h-3 w-3" /></Link></Button>
            </div>
          )}

          {view === "month" && (
            <MonthBoard
              cursor={cursor}
              monthCells={monthCells}
              itemsByDay={itemsByDay}
              hoverDay={hoverDay}
              dragging={dragging}
              setHoverDay={setHoverDay}
              setDragging={setDragging}
              handleDrop={handleDrop}
              setSelected={setSelected}
            />
          )}

          {view === "week" && (
            <WeekBoard
              weekDays={weekDays}
              itemsByDay={itemsByDay}
              hoverDay={hoverDay}
              setHoverDay={setHoverDay}
              setDragging={setDragging}
              handleDrop={handleDrop}
              setSelected={setSelected}
            />
          )}

          {view === "list" && (
            <ListBoard items={filteredItems} setSelected={setSelected} />
          )}
        </main>

        <aside className="space-y-4">
          <PipelinePanel metrics={workspace.metrics} />
          <PlatformCoveragePanel platformCounts={workspace.platformCounts} />
        </aside>
      </section>

      <CalendarInspector
        selected={selected}
        selectedAsset={selectedAsset}
        setSelected={setSelected}
        publishCalendarItem={publishCalendarItem}
        publishAsset={publishAsset}
        armCalendarItem={armCalendarItem}
        schedulerStatus={schedulerStatus}
      />
    </motion.div>
  );
}

function CalendarToolbar({ view, setView, platform, setPlatform, cursor, onPrev, onNext, onToday }) {
  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--border-subtle)] bg-card p-3 shadow-[0_6px_16px_rgba(18, 22, 20,0.05)]" data-testid="calendar-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="icon" variant="outline" onClick={onPrev} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" onClick={onNext} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={onToday} className="gap-1.5 text-muted-foreground hover:text-foreground" data-testid="calendar-today"><RefreshCw className="h-3.5 w-3.5" /> Today</Button>
        <div className="ml-1 flex items-center gap-2 text-sm font-semibold text-foreground">
          <CalendarRange className="h-4 w-4 text-[color:var(--accent-cyan)]" />
          {view === "month" && cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          {view === "week" && `Week of ${formatDate(cursor, { year: true })}`}
          {view === "list" && "Launch list"}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="gap-1 bg-[color:var(--surface-3)] p-1" data-testid="calendar-view-tabs">
            <TabsTrigger value="month" className="data-[state=active]:text-foreground" data-testid="calendar-view-month">Month</TabsTrigger>
            <TabsTrigger value="week" className="data-[state=active]:text-foreground" data-testid="calendar-view-week">Week</TabsTrigger>
            <TabsTrigger value="list" className="data-[state=active]:text-foreground" data-testid="calendar-view-list">List</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="h-9 w-[170px] border-[color:var(--border-subtle)] bg-card text-foreground" data-testid="calendar-platform-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All platforms</SelectItem>
            {PLATFORMS.map((item) => (
              <SelectItem key={item} value={item}>{PLATFORM_LABELS[item] || item}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </section>
  );
}

function MonthBoard({ cursor, monthCells, itemsByDay, hoverDay, dragging, setHoverDay, setDragging, handleDrop, setSelected }) {
  return (
    <section className="overflow-hidden rounded-xl border border-[color:var(--border-subtle)] bg-card shadow-[0_18px_50px_rgba(18, 22, 20,0.08)]" data-testid="calendar-month-grid">
      <div className="grid grid-cols-7 border-b border-[color:var(--border-subtle)] bg-[#0C0F0E] text-white">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 bg-[color:var(--surface-3)] gap-px">
        {monthCells.map((cell, idx) => {
          const isCurrentMonth = cell.getMonth() === cursor.getMonth();
          const today = sameDay(cell, new Date());
          const key = dayKey(cell);
          const dayItems = itemsByDay.get(key) || [];
          const isHover = hoverDay === key;
          const isDragSource = dragging && sameDay(new Date(dragging.scheduled_at), cell);
          return (
            <div
              key={idx}
              data-testid={`calendar-day-${cell.toISOString().slice(0, 10)}`}
              onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; if (hoverDay !== key) setHoverDay(key); }}
              onDragLeave={() => { if (hoverDay === key) setHoverDay(null); }}
              onDrop={(event) => handleDrop(event, cell)}
              className={cn("relative min-h-[132px] p-2 transition", isHover && !isDragSource && "ring-2 ring-inset ring-[color:var(--accent-cyan)]")}
              style={{ backgroundColor: isHover && !isDragSource ? "rgba(18, 147, 79,0.08)" : isCurrentMonth ? "#FFFFFF" : "#F8FAFC" }}
            >
              <div className="mb-1.5 flex items-center justify-between">
                <span className={cn("flex h-6 min-w-6 items-center justify-center rounded-md text-[12px] font-semibold", today ? "bg-[color:var(--accent-blue)] text-[color:var(--accent-blue-ink)]" : isCurrentMonth ? "text-[color:var(--text-2)]" : "text-muted-foreground")}>
                  {cell.getDate()}
                </span>
                {dayItems.length > 0 && (
                  <span className="rounded-full bg-[color:var(--surface-3)] px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{dayItems.length}</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                {dayItems.slice(0, 3).map((item) => (
                  <CalendarItem
                    key={item.id}
                    item={item}
                    dense
                    draggable
                    onDragStart={(dragItem) => setDragging(dragItem)}
                    onDragEnd={() => { setDragging(null); setHoverDay(null); }}
                    onClick={() => setSelected(item)}
                  />
                ))}
                {dayItems.length > 3 && (
                  <button type="button" className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] px-2 py-1 text-left text-[11px] font-medium text-muted-foreground hover:bg-[color:var(--accent-blue)]/12 hover:text-[color:var(--accent-cyan)]" onClick={() => setSelected(dayItems[3])}>
                    +{dayItems.length - 3} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function WeekBoard({ weekDays, itemsByDay, hoverDay, setHoverDay, setDragging, handleDrop, setSelected }) {
  return (
    <section className="space-y-3" data-testid="calendar-week-grid">
      {weekDays.map((date) => {
        const key = dayKey(date);
        const dayItems = itemsByDay.get(key) || [];
        const isHover = hoverDay === key;
        const today = sameDay(date, new Date());
        return (
          <div
            key={date.toISOString()}
            onDragOver={(event) => { event.preventDefault(); if (hoverDay !== key) setHoverDay(key); }}
            onDragLeave={() => { if (hoverDay === key) setHoverDay(null); }}
            onDrop={(event) => handleDrop(event, date)}
            className={cn("grid grid-cols-1 gap-3 rounded-xl border border-[color:var(--border-subtle)] bg-card p-3 shadow-[0_14px_34px_rgba(18, 22, 20,0.06)] transition md:grid-cols-[96px_minmax(0,1fr)]", isHover && "ring-2 ring-inset ring-[color:var(--accent-cyan)]")}
            data-testid={`calendar-week-day-${date.toISOString().slice(0, 10)}`}
          >
            <div className={cn("flex items-center justify-between rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/80 px-3 py-3 md:block", today && "border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12")}>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{date.toLocaleDateString(undefined, { weekday: "short" })}</p>
                <p className="text-2xl font-bold tracking-[-0.03em] text-foreground">{date.getDate()}</p>
              </div>
              {today && <span className="rounded-full border border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[color:var(--accent-cyan)]">Today</span>}
            </div>
            <div className="grid min-h-[86px] grid-cols-1 gap-2 lg:grid-cols-2">
              {dayItems.length === 0 ? (
                <div className="flex min-h-[82px] items-center justify-center rounded-lg border border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/70 px-3 text-center text-[12px] text-muted-foreground lg:col-span-2">
                  Open slot
                </div>
              ) : dayItems.map((item) => (
                <CalendarItem
                  key={item.id}
                  item={item}
                  draggable
                  onDragStart={(dragItem) => setDragging(dragItem)}
                  onDragEnd={() => { setDragging(null); setHoverDay(null); }}
                  onClick={() => setSelected(item)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function ListBoard({ items, setSelected }) {
  return (
    <section className="overflow-hidden rounded-xl border border-[color:var(--border-subtle)] bg-card shadow-[0_18px_48px_rgba(18, 22, 20,0.07)]" data-testid="calendar-list">
      <div className="border-b border-[color:var(--border-subtle)] bg-[#0C0F0E] px-4 py-3 text-white">
        <p className="text-sm font-semibold">Launch runway</p>
        <p className="text-xs text-slate-300">Every scheduled slot, ordered by publish time.</p>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((item) => (
          <li key={item.id}>
            <button type="button" onClick={() => setSelected(item)} className="grid w-full grid-cols-1 gap-3 px-4 py-4 text-left transition hover:bg-[color:var(--accent-blue)]/12/40 md:grid-cols-[140px_minmax(0,1fr)_190px_auto]" data-testid={`calendar-list-item-${item.id}`}>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{formatDate(item.scheduled_at, { year: false })}</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{formatTime(item.scheduled_at)}</p>
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <PlatformBadge platform={item.platform} />
                  <StatusBadge status={item.status} />
                </div>
                <p className="mt-2 truncate text-sm font-semibold text-foreground">{item.title}</p>
              </div>
              <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                <RadioTower className={cn("h-4 w-4 shrink-0", item.publishingContext?.connected ? "text-[color:var(--accent-green)]" : "text-muted-foreground")} />
                <span className="truncate">{item.publishingContext?.accountLabel}</span>
              </div>
              <ArrowRight className="hidden h-4 w-4 self-center text-muted-foreground md:block" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PipelinePanel({ metrics }) {
  const stages = [
    { label: "Drafts", value: metrics.needsReview, icon: ClipboardCheck, tone: "amber" },
    { label: "Scheduled", value: metrics.scheduled, icon: CalendarClock, tone: "blue" },
    { label: "Published", value: metrics.published, icon: CheckCircle2, tone: "green" },
    { label: "Failed", value: metrics.failed, icon: AlertTriangle, tone: "red" },
  ];

  return (
    <section className="rounded-xl border border-[color:var(--border-subtle)] bg-card p-4 shadow-[0_6px_16px_rgba(18, 22, 20,0.05)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">Calendar pipeline</h2>
          <p className="mt-1 text-sm text-muted-foreground">Status of items on this calendar (not the full asset library).</p>
        </div>
        <Route className="h-5 w-5 text-[color:var(--accent-cyan)]" />
      </div>
      <div className="space-y-2">
        {stages.map(({ label, value, icon: Icon, tone }) => (
          <div key={label} className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/70 px-3 py-3">
            <span className="flex items-center gap-3">
              <span className={cn("flex h-8 w-8 items-center justify-center rounded-md border", toneClass(tone))}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="text-sm font-semibold text-foreground">{label}</span>
            </span>
            <span className="text-xl font-bold tracking-[-0.03em] text-foreground">{value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PlatformCoveragePanel({ platformCounts }) {
  const platforms = Object.entries(platformCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <section className="rounded-xl border border-[color:var(--border-subtle)] bg-card p-4 shadow-[0_6px_16px_rgba(18, 22, 20,0.05)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">Channel coverage</h2>
          <p className="mt-1 text-sm text-muted-foreground">Scheduled activity by endpoint.</p>
        </div>
        <RadioTower className="h-5 w-5 text-[color:var(--accent-cyan)]" />
      </div>
      <div className="space-y-2">
        {platforms.map(([platform, count]) => (
          <div key={platform} className="flex items-center justify-between rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/70 px-3 py-2.5">
            <PlatformBadge platform={platform} />
            <span className="text-sm font-bold text-foreground">{count}</span>
          </div>
        ))}
        {platforms.length === 0 && (
          <p className="rounded-lg border border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/70 px-4 py-6 text-center text-sm text-muted-foreground">No scheduled channels yet.</p>
        )}
      </div>
    </section>
  );
}

function CalendarInspector({ selected, selectedAsset, setSelected, publishCalendarItem, publishAsset, armCalendarItem, schedulerStatus }) {
  const context = selected?.publishingContext;
  const [publishing, setPublishing] = useState(false);

  const armed = selected?.publish_mode === "auto";
  // Only platforms with a wired publish action can auto-post. If we don't know
  // yet (status not loaded), don't block.
  const publishable = schedulerStatus?.publishable;
  const canAutoPublish = !publishable || publishable.includes(selected?.platform);
  const onToggleArm = (next) => {
    armCalendarItem(selected.id, next);
    setSelected((current) => (current ? { ...current, publish_mode: next ? "auto" : "manual" } : current));
    toast[next ? "success" : "message"](next ? "Armed for auto-publish at the scheduled time" : "Auto-publish turned off");
  };

  const copyPost = () => {
    if (!selectedAsset) return toast.message("No source asset attached to copy.");
    const text = [selectedAsset.hook, selectedAsset.body, selectedAsset.cta, (selectedAsset.hashtags || []).join(" ")]
      .filter(Boolean)
      .join("\n\n");
    navigator.clipboard?.writeText(text);
    toast.success("Copied post copy");
  };

  const markPublishedLocal = () => {
    publishCalendarItem(selected.id);
    setSelected((current) => (current ? { ...current, status: "published", published_at: new Date().toISOString() } : current));
  };

  // Real publish via the backend (Composio) when a source asset is attached;
  // on success also mark the calendar item published. Manual entries (no asset)
  // fall back to a plain "mark published".
  const onPublish = async () => {
    if (!selectedAsset) {
      markPublishedLocal();
      toast.success("Marked published");
      return;
    }
    setPublishing(true);
    const pending = toast.loading(`Publishing to ${selected.platform}…`);
    try {
      const result = await publishAsset(selectedAsset.id);
      if (result?.ok) {
        markPublishedLocal();
        toast.success(`Published to ${selected.platform}`, { id: pending });
      } else {
        if (result?.content) navigator.clipboard?.writeText(result.content);
        toast.error(`${result?.message || "Publish failed."} Copied the post so you can publish manually.`, { id: pending });
      }
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
      <SheetContent side="right" className="w-full max-w-[460px] overflow-y-auto border-[color:var(--border-subtle)] bg-card p-0 text-foreground" data-testid="calendar-inspector-sheet">
        {selected && (
          <>
            <div className="relative overflow-hidden bg-[#0C0F0E] px-6 py-6 text-white">
              <div aria-hidden className="absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(150, 160, 152,0.20)_1px,transparent_1px),linear-gradient(90deg,rgba(150, 160, 152,0.14)_1px,transparent_1px)] [background-size:32px_32px]" />
              <SheetHeader className="relative pr-7 text-left">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <PlatformBadge platform={selected.platform} />
                  <StatusBadge status={selected.status} />
                  {armed && selected.status !== "published" && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium" style={{ backgroundColor: "rgba(245,158,11,0.16)", color: "#FBBF24" }} data-testid="calendar-inspector-auto-badge">
                      <RadioTower className="h-3 w-3" /> Auto
                    </span>
                  )}
                </div>
                <SheetTitle className="text-xl font-bold leading-tight tracking-[-0.025em] text-white">{selected.title}</SheetTitle>
                <SheetDescription className="text-slate-300">{formatDateTime(selected.scheduled_at)}</SheetDescription>
              </SheetHeader>
            </div>

            <div className="space-y-4 px-6 py-5">
              <InfoCard
                icon={Clock3}
                label="Publish slot"
                title={formatDate(selected.scheduled_at, { year: true })}
                detail={formatTime(selected.scheduled_at)}
              />
              <InfoCard
                icon={RadioTower}
                label="Publishing mode"
                title={context?.modeLabel || "Publishing queue"}
                detail={context?.accountLabel || "No account selected"}
                good={context?.connected}
              />
              {selected.schedule_notes || selectedAsset?.schedule_notes ? (
                <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/70 p-3">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Posting notes</p>
                  <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-[color:var(--text-2)]">{selected.schedule_notes || selectedAsset?.schedule_notes}</p>
                </div>
              ) : null}

              {selected.status !== "published" && (
                <div className="rounded-lg border p-3" style={{ borderColor: armed ? "rgba(245,158,11,0.4)" : "var(--border-subtle)", backgroundColor: armed ? "rgba(245,158,11,0.06)" : "transparent" }} data-testid="calendar-inspector-autopublish">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-foreground">Auto-publish at scheduled time</p>
                      <p className="mt-0.5 text-[11.5px] text-muted-foreground">Posts automatically — no further review. Off by default.</p>
                    </div>
                    <Switch checked={armed} onCheckedChange={onToggleArm} disabled={!canAutoPublish} data-testid="calendar-inspector-arm" />
                  </div>
                  {!canAutoPublish ? (
                    <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="calendar-inspector-autopublish-unavailable">
                      Auto-publish isn't available for {selected.platform} yet — it's connect-only (needs video) or requires posting manually.
                    </p>
                  ) : armed && (() => {
                    const engineOff = schedulerStatus && !schedulerStatus.enabled;
                    const igNeedsUrl = selected.platform === "instagram" && schedulerStatus && schedulerStatus.public_url_set === false;
                    const warn = engineOff || igNeedsUrl;
                    return (
                      <p className="mt-2 text-[11.5px]" style={{ color: warn ? "#B45309" : "#15803D" }}>
                        {engineOff
                          ? "⚠ The auto-publish engine is currently OFF on the server (AUTO_PUBLISH_ENABLED). This will publish once it's enabled."
                          : igNeedsUrl
                            ? "⚠ Instagram posts a generated text card, which needs PUBLIC_BASE_URL set on the server so Meta can fetch the image. It'll publish once that's configured."
                            : selected.platform === "instagram"
                              ? "✓ Armed. We'll post a generated text card + caption at the scheduled time."
                              : "✓ Armed. This will post at the scheduled time without further review."}
                      </p>
                    );
                  })()}
                </div>
              )}

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {selected.status !== "published" && selectedAsset ? (
                  <ScheduleAssetDialog
                    asset={selectedAsset}
                    trigger={(
                      <Button size="sm" variant="outline" className="h-10 gap-1.5" data-testid="calendar-inspector-reschedule">
                        <CalendarRange className="h-3.5 w-3.5" /> Reschedule
                      </Button>
                    )}
                  />
                ) : selected.status !== "published" ? (
                  <Button size="sm" variant="outline" className="h-10 gap-1.5" onClick={() => toast.message("Tip: drag the item to another day to reschedule")}>
                    <CalendarRange className="h-3.5 w-3.5" /> Reschedule
                  </Button>
                ) : null}
                {selectedAsset && (
                  <Button asChild size="sm" variant="outline" className="h-10 gap-1.5" data-testid="calendar-inspector-open-asset">
                    <Link to={`/library/${selectedAsset.id}`}><ExternalLink className="h-3.5 w-3.5" /> Open post</Link>
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-10 gap-1.5" onClick={copyPost} data-testid="calendar-inspector-copy">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
                {selected.status !== "published" ? (
                  <Button
                    size="sm"
                    className="h-10 gap-1.5 bg-[#0C0F0E] text-white hover:bg-slate-800"
                    onClick={onPublish}
                    disabled={publishing}
                    data-testid="calendar-inspector-publish"
                  >
                    {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {selectedAsset ? "Publish now" : "Mark published"}
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-10 gap-1.5" disabled data-testid="calendar-inspector-published">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Published
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function InfoCard({ icon: Icon, label, title, detail, good = false }) {
  return (
    <div className="rounded-lg border border-[color:var(--border-subtle)] bg-card p-3 shadow-[0_8px_20px_rgba(18, 22, 20,0.04)]">
      <p className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        <Icon className={cn("h-3.5 w-3.5", good ? "text-[color:var(--accent-green)]" : "text-[color:var(--accent-cyan)]")} />
        {label}
      </p>
      <p className="mt-2 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

function toneClass(tone) {
  const map = {
    amber: "border-amber-200 bg-[color:var(--accent-amber)]/12 text-[color:var(--accent-amber)]",
    blue: "border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
    green: "border-emerald-200 bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]",
    red: "border-red-200 bg-red-50 text-red-700",
  };
  return map[tone] || map.blue;
}
